import React, { useState } from 'react';
import { shortAddress } from './cryptoFormat';
import { useT } from '../../i18n';

// The tracked wallets, and the form that adds one.
//
// On the holdings page itself rather than the settings page: an address is not
// a credential or a connection to configure, it is the thing being looked at,
// and adding one should show its holdings where you added it.

// Hyperliquid is an exchange, not a chain, but an account there is an address
// like any other: added the same way, read from the exchange's own API.
export const CHAINS = ['ethereum', 'arbitrum', 'base', 'solana', 'hyperliquid'];

const PROVIDER = { ethereum: 'Alchemy', arbitrum: 'Alchemy', base: 'Alchemy', solana: 'Helius', hyperliquid: 'Hyperliquid' };

export const walletName = (w) => w.label || shortAddress(w.address);

function status(t, fmt, w) {
    if (!w.configured) return { text: t('crypto.wallets.notConfigured', { provider: PROVIDER[w.chain] }), tone: 'warn' };
    if (w.balanceError) return { text: t('crypto.wallets.balanceError'), tone: 'warn' };
    if (w.syncError) return { text: t('crypto.wallets.syncError'), tone: 'warn' };
    if (!w.historyComplete) return { text: t('crypto.wallets.indexing'), tone: 'busy' };
    return { text: t('crypto.wallets.synced', { when: fmt.dateTime(w.syncedAt) }), tone: 'ok' };
}

export default function CryptoWallets({ wallets, onAdd, onRename, onRemove, busy, error }) {
    const { t, fmt } = useT();
    const [form, setForm] = useState({ chain: 'ethereum', address: '', label: '' });
    // Which wallet is being renamed, and the draft — the settings page's
    // pattern for a connection nickname, so the two behave alike.
    const [renaming, setRenaming] = useState(null);
    const [draft, setDraft] = useState('');

    const saveName = async (e, id) => {
        e.preventDefault();
        if (await onRename(id, draft.trim())) setRenaming(null);
    };

    const submit = async (e) => {
        e.preventDefault();
        if (await onAdd(form)) setForm({ ...form, address: '', label: '' });
    };

    const remove = (w) => {
        if (window.confirm(t('crypto.wallets.confirmRemove', { name: walletName(w) }))) onRemove(w.id);
    };

    return (
        <div className="crypto__wallets">
            <h2 className="notes__h">{t('crypto.wallets.heading')}</h2>

            {wallets.length === 0 ? (
                <p className="notes__empty">{t('crypto.wallets.empty')}</p>
            ) : (
                <ul className="crypto__wallet_list">
                    {wallets.map((w) => {
                        const s = status(t, fmt, w);
                        return (
                            <li key={w.id}>
                                <span className="crypto__wallet_chain">{t(`crypto.chains.${w.chain}`)}</span>
                                {renaming === w.id ? (
                                    <form className="settings__rename crypto__wallet_name" onSubmit={(e) => saveName(e, w.id)}>
                                        <input
                                            type="text"
                                            maxLength={60}
                                            value={draft}
                                            autoFocus
                                            placeholder={shortAddress(w.address)}
                                            onChange={(e) => setDraft(e.target.value)}
                                            aria-label={t('crypto.wallets.nameAria')}
                                        />
                                        <button type="submit" className="settings__rename_go" disabled={busy}>
                                            {t('common.save')}
                                        </button>
                                        <button type="button" className="settings__rename_go" onClick={() => setRenaming(null)}>
                                            {t('common.cancel')}
                                        </button>
                                    </form>
                                ) : (
                                    <span className="crypto__wallet_name" title={w.address}>
                                        {walletName(w)}
                                        {w.label && <span className="crypto__wallet_addr">{shortAddress(w.address)}</span>}
                                        <button
                                            type="button"
                                            className="settings__rename_open"
                                            onClick={() => { setRenaming(w.id); setDraft(w.label || ''); }}
                                        >
                                            {t('crypto.wallets.rename')}
                                        </button>
                                    </span>
                                )}
                                <span className={`crypto__wallet_status is-${s.tone}`}>{s.text}</span>
                                <button
                                    type="button"
                                    className="notes__link is-danger"
                                    disabled={busy}
                                    onClick={() => remove(w)}
                                    aria-label={t('crypto.wallets.removeAria', { name: walletName(w) })}
                                >
                                    {t('crypto.wallets.remove')}
                                </button>
                            </li>
                        );
                    })}
                </ul>
            )}

            <form className="crypto__add" onSubmit={submit}>
                <select
                    className="themes__select"
                    value={form.chain}
                    onChange={(e) => setForm({ ...form, chain: e.target.value })}
                    aria-label={t('crypto.wallets.chainAria')}
                >
                    {CHAINS.map((c) => <option key={c} value={c}>{t(`crypto.chains.${c}`)}</option>)}
                </select>
                <input
                    type="text"
                    className="positions__search crypto__address"
                    placeholder={t('crypto.wallets.addressPlaceholder')}
                    value={form.address}
                    onChange={(e) => setForm({ ...form, address: e.target.value })}
                    aria-label={t('crypto.wallets.addressAria')}
                    spellCheck={false}
                    autoComplete="off"
                />
                <input
                    type="text"
                    className="positions__search"
                    placeholder={t('crypto.wallets.labelPlaceholder')}
                    value={form.label}
                    maxLength={60}
                    onChange={(e) => setForm({ ...form, label: e.target.value })}
                    aria-label={t('crypto.wallets.labelAria')}
                />
                <button type="submit" className="positions__filter" disabled={busy || !form.address.trim()}>
                    {t('crypto.wallets.add')}
                </button>
            </form>
            {error && <p className="holdings__error" role="alert">{t(error)}</p>}
        </div>
    );
}
